<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox
          title="Production Stage"
          :loading="pageDataLoading"
          :avatarImage="logoURL"
          :avatarName="stage.name"
          :avatarCircleMode="avatarCircleMode"
          :avatarEditable="userHasUpdatePermission(PermissionTypes.ProductionPermissionTypes.productionConfig)"
          @editImage="editAvatarImage"
          @removeImage="removeAvatarImage">
          <h4>{{stage.name}}</h4>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="Status">
              <span v-if="stage.isActive" class="status-active">Active</span>
              <span v-if="!stage.isActive" class="status-archive">Archived</span>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Highlight">
              <LabelBadge :labelColour="stage.highlightColour">{{stage.name}}</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Category">{{stage.category | formatLookupName(ProductionStageCategoryTypeLookup)}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Created On">{{stage.createdAt | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated">{{stage.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

      </div>

      <div class="col-sm-10">
        <TabbedBox ref="tabBox" :loading="pageDataLoading">
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-stage-summary">
              <i class="far fa-clipboard-list-check"></i> Stage Details
            </Tab>
            <Tab tabid="tab-stage-production">
              <i class="far fa-tools"></i> Production
            </Tab>
            <Tab tabid="tab-stage-toughening" v-if="isTougheningStage">
              <i class="far fa-hammer"></i> Toughening Batches
            </Tab>
            <Tab tabid="tab-stage-activitylog">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>
          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-stage-summary">
              
              <DataPageArea title="Details">
                <template slot="actionArea">
                  <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.ProductionPermissionTypes.productionConfig)" class="float-right">
                    <DropdownMenuItem @click="editStage" icon="fa-pencil">Edit Production Stage</DropdownMenuItem>
                    <DropdownMenuItem @click="newProductionStation" icon="fa-plus">Add Station</DropdownMenuItem>
                    <DropdownMenuDivider />
                    <DropdownMenuItem @click="editComplianceImage" icon="fa-image">Edit Compliance Image</DropdownMenuItem>
                    <DropdownMenuItem @click="removeComplianceImage" icon="fa-trash-alt" v-if="stage.complianceLabelImageFileId">Remove Compliance Image</DropdownMenuItem>
                    <template v-if="isTougheningStage">
                      <DropdownMenuItem @click="editAutoComplianceImage" icon="fa-image">Edit Automotive Image</DropdownMenuItem>
                      <DropdownMenuItem @click="removeAutoComplianceImage" icon="fa-trash-alt" v-if="stage.autoComplianceLabelImageFileId">Remove Automotive Image</DropdownMenuItem>
                    </template>

                    <DropdownMenuDivider />
                    <DropdownMenuItem @click="archiveStage" v-if="stage.isActive" icon="fa-archive">Archive Production Stage</DropdownMenuItem>
                    <DropdownMenuItem @click="restoreStage" v-if="!stage.isActive" icon="fa-undo">Restore Production Stage</DropdownMenuItem>
                  </ActionDropMenu>
                </template>
                <template slot="dataArea">
                  <DataItem title="Name">{{stage.name}}</DataItem>
                  <DataItem title="Category">{{stage.category | formatLookupName(ProductionStageCategoryTypeLookup)}}</DataItem>

                  <DataItem title="Compliance Label Image" v-if="stage.complianceLabelLogoURL">
                    <a :href="stage.complianceLabelLogoURL" target="_blank">Label Image</a>
                  </DataItem>
                  <DataItem title="Automotive Label Image" v-if="stage.autoComplianceLabelLogoURL">
                    <a :href="stage.autoComplianceLabelLogoURL" target="_blank">Label Image</a>
                  </DataItem>
                </template>
              </DataPageArea>

              <FormHorizontalRule />

              <div class="row mt-2">
                <div class="col-12">
                  <ContentTitle>Stage Rules / Settings</ContentTitle>
                  <ContentSubTitle>Controls the rules and settings for production and associated stations at this stage</ContentSubTitle>
                </div>
              </div>
              <div class="row">
                <div class="col-6">
                  <PropertyValueTable propertyTitle="Scheduling">
                    <PropertyValueRow name="Lead Time Type">{{stage.leadTimeType | formatLookupName(ProductionStageLeadTimeTypeLookup)}}</PropertyValueRow>
                    <PropertyValueRow name="Static Lead Time" v-if="isStaticLeadTimeType">{{stage.leadTime}} mins</PropertyValueRow>
                    <PropertyValueRow name="Min. Dynamic Lead Time" v-if="isDynamicLeadTimeType">{{stage.dynamicLeadTimeMinimum}} mins</PropertyValueRow>
                    <PropertyValueRow name="Dynamic Lead Time Warning Level" v-if="isDynamicLeadTimeType">{{stage.dynamicLeadTimeWarningThreshold}} mins</PropertyValueRow>
                    <PropertyValueRow name="Current Lead Time" v-if="isDynamicLeadTimeType">{{stage.leadTime}} mins</PropertyValueRow>
                  </PropertyValueTable>

                  <PropertyValueTable propertyTitle="Rules">
                    <PropertyValueRow name="Is Stage Tag Shown">{{stage.isStageTagShown | formatBoolean}}</PropertyValueRow>
                    <PropertyValueRow name="External Stage">{{stage.isExternal | formatBoolean}}</PropertyValueRow>
                    <PropertyValueRow name="Floating Stage">{{stage.isFloatingStage | formatBoolean}}</PropertyValueRow>
                    <PropertyValueRow name="Auto Complete Stage">{{stage.isAutoCompleteStage | formatBoolean}}</PropertyValueRow>
                    <PropertyValueRow name="Assembly Processes Child Items" v-if="isAssemblyStage">{{stage.isAssemblyProcessingChildItems | formatBoolean}}</PropertyValueRow>
                    <PropertyValueRow name="Requires Scan In">{{stage.requiresScanInAtStage | formatBoolean}}</PropertyValueRow>
                    <PropertyValueRow name="Requires Scan Out">{{stage.requiresScanOutAtStage | formatBoolean}}</PropertyValueRow>
                  </PropertyValueTable>
                </div>
                <div class="col-6">
                  <PropertyValueTable propertyTitle="Printer Settings">
                    <PropertyValueRow name="Requires Stage Label Printer Setup">{{stage.requiresStageLabelPrinter | formatBoolean}}</PropertyValueRow>
                    <PropertyValueRow name="Requires Post Label Printer Setup">{{stage.requiresPostStageLabelPrinter | formatBoolean}}</PropertyValueRow>
                    <PropertyValueRow name="Print Label On Scan In">{{stage.printLabelOnStageScanIn | formatBoolean}}</PropertyValueRow>
                    <PropertyValueRow name="Print Label On Scan Out">{{stage.printLabelOnStageScanOut | formatBoolean}}</PropertyValueRow>
                    <PropertyValueRow name="Requires Stage Order Printer Setup">{{stage.requiresStageOrderPrinter | formatBoolean}}</PropertyValueRow>
                  </PropertyValueTable>
                </div>
              </div>

              <FormHorizontalRule />

              <div class="row mt-2">
                <div class="col-lg-12">
                  <ContentTitle>Stations</ContentTitle>
                  <ContentSubTitle>Production stations associated with this production stage</ContentSubTitle>
                  <StaticDataTable v-if="stage.productionStations">
                    <template v-slot:header>
                      <tr>
                        <th width="40%">Station</th>
                        <th>Type</th>
                        <th>Unit Of Measure</th>
                        <th>Capacity</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="station of stage.productionStations" :key="station.id">
                        <td>
                          <LinkProductionStation :id="station.id" :name="station.name" />
                        </td>
                        <td>{{station.type | formatLookupName(ProductionStationTypeLookup)}}</td>
                        <td><span v-html="formatLookupName(station.uomType, UOMTypeLookup)" /></td>
                        <td>
                          <div v-if="station.isDependentStationType">N/A</div>
                          <div v-else>
                            {{station.capacityUnitsPerHour}} <span v-html="formatLookupName(station.uomType, UOMTypeUnitsLookup)"></span> / hour
                          </div>
                        </td>
                      </tr>
                      <tr class="font-weight-bold">
                        <td>Total</td>
                        <td></td>
                        <td></td>
                        <td>
                          {{totalStationCapacitorUnitsPerHour}} <span v-html="formatLookupName(stage.productionStations[0] ? stage.productionStations[0].uomType : null, UOMTypeUnitsLookup)"></span> / hour
                        </td>
                      </tr>
                    </template>
                  </StaticDataTable>
                </div>
              </div>

            </TabPage>

            <TabPage tabid="tab-stage-production">
              <div class="row">
                <div class="col-lg-12">
                  <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.ProductionPermissionTypes.productionConfig)" class="float-right">
                    <DropdownMenuItem @click="editProductionDetails" icon="fa-pencil">Edit Production Details</DropdownMenuItem>
                  </ActionDropMenu>

                  <ContentTitle>Associated Stock Categories</ContentTitle>
                  <ContentSubTitle>All glass service stock under these categories will result in this production stage being added to the production task list for those order items</ContentSubTitle>
                  <StaticDataTable v-if="stage.stockCategories">
                    <template v-slot:header>
                      <tr>
                        <th>Name</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="stockCategory of stage.stockCategories" :key="stockCategory.id">
                        <td>
                          <LinkStockCategory :id="stockCategory.id" :name="stockCategory.name" />
                        </td>                        
                      </tr>
                    </template>
                    <template v-slot:empty v-if="stage.stockCategories.length == 0">
                      No stock categories associated with the production stage
                    </template>
                  </StaticDataTable>
                </div>
              </div>

              <FormHorizontalRule />

              <div class="row mt-4">
                <div class="col-lg-12">
                  <ContentTitle>Associated Price Groups</ContentTitle>
                  <ContentSubTitle>All order items using these price groups will result in this production stage being added to the production task list for the order items</ContentSubTitle>
                  <StaticDataTable v-if="stage.stockPriceGroups">
                    <template v-slot:header>
                      <tr>
                        <th>Name</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="priceGroup of stage.stockPriceGroups" :key="priceGroup.id">
                        <td>
                          <LinkStockPriceGroup :id="priceGroup.id" :name="priceGroup.name" />
                        </td>                        
                      </tr>
                    </template>
                    <template v-slot:empty v-if="stage.stockPriceGroups.length == 0">
                      No price groups associated with the production stage
                    </template>
                  </StaticDataTable>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-stage-toughening" v-if="isTougheningStage">
              <TougheningBatchDataTable :productionStageId="stage.id" />
            </TabPage>

            <TabPage tabid="tab-stage-activitylog">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.productionStage" :linkId="stage.id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'

import TougheningBatchDataTable from '@/modules/production/components/tougheningBatchDataTable'
import EditLogoImageDialog from '@/modules/core/views/editLogoImageDialog.vue'

import ProductionStageDialog from '@/modules/production/views/productionStageDialog'
import ProductionStageLinksDialog from '@/modules/production/views/productionStageLinkDialog'
import ProductionStationDialog from '@/modules/production/views/productionStationDialog'

import { LinkTypes, ProductionStageCategoryTypes, ProductionStageCategoryTypeLookup, ProductionStationTypeLookup, UOMTypeLookup, UOMTypeUnitsLookup, ProductionStageLeadTimeTypes, ProductionStageLeadTimeTypeLookup } from '@/store/types'

export default {
  name: 'ProductionStage',
  mixins: [form],
  components: {
    TougheningBatchDataTable
  },
  data() {
    return {
      stage: {}
    }
  },
  computed: {
    logoURL() {
      if(this.$data.stage.logoURL == null || this.$data.stage.logoURL == '') {
        return '/glass/img/production-overview-stage.png'
      }
      return this.$data.stage.logoURL
    },
    avatarCircleMode() {
      return this.$data.stage.logoURL != null && this.$data.stage.logoURL != ''
    },
    activityStreamURL() {
      return `/production/stages/${this.$route.params.id}/activitystream`
    },
    isTougheningStage() {
      return this.$data.stage.category == ProductionStageCategoryTypes.furnace
    },
    isAssemblyStage() {
      return this.$data.stage.category == ProductionStageCategoryTypes.assembly
    },
    isStaticLeadTimeType() {
      return this.$data.stage.leadTimeType == ProductionStageLeadTimeTypes.static
    },
    isDynamicLeadTimeType() {
      return this.$data.stage.leadTimeType == ProductionStageLeadTimeTypes.dynamic
    },
    totalStationCapacitorUnitsPerHour() {
      if(this.stage.productionStations == null || this.stage.productionStations.length == 0) {
        return 0
      }

      return this.stage.productionStations.reduce((total, station) => {
        if(station.isDependentStationType) {
          return total
        }
        return total + station.capacityUnitsPerHour
      }, 0)
    }
  },
  created() {
    this.LinkTypes = LinkTypes
    this.ProductionStageCategoryTypeLookup = ProductionStageCategoryTypeLookup
    this.ProductionStationTypeLookup = ProductionStationTypeLookup
    this.UOMTypeLookup = UOMTypeLookup
    this.UOMTypeUnitsLookup = UOMTypeUnitsLookup
    this.ProductionStageLeadTimeTypeLookup = ProductionStageLeadTimeTypeLookup
  },
  mounted() {
    this.requestData()
  },
  methods: {
    reloadData() {
      this.$refs.activityStream.reloadData()
      this.requestData()
    },
    requestData() {
      this.pageLoadData(Promise.all([
        this.$apiService.productionService.getProductionStage(this.$route.params.id),
      ]))
      .then(results => {
        this.stage = results[0].data
      })
      .catch(err => this.handlePageLoadError(err))
    },

    editStage() {
      this.displayModal(ProductionStageDialog, { 
        productionStageId: this.$data.stage.id,
      },
      this.$data.stage)
      .then((success) => {
        if(success) {
          this.successToast('Production Stage Updated')
          this.reloadData()
        }
      })
    },
    archiveStage() {
      this.confirmDeleteMessage('Archive Production Stage?', 'Are you sure you want to archive this production stage?')
      .then(success => {
        if(success) {
          this.$apiService.productionService.archiveProductionStage(this.$route.params.id).then(response => {
            this.successToast('Production Stage Archived')
            this.reloadData()
          })
          .catch(err => { this.displayNetworkError(err, 'Error', 'Could not archive production stage') })
        }
      })
    },
    restoreStage() {
      this.confirmDeleteMessage('Restore Production Stage?', 'Are you sure you want to restore this production stage?')
      .then(success => {
        if(success) {
          this.$apiService.productionService.restoreProductionStage(this.$route.params.id).then(response => {
            this.successToast('Production Stage Restored')
            this.reloadData()
          })
          .catch(err => { this.displayNetworkError(err, 'Error', 'Could not restore production stage') })
        }
      })
    },

    editProductionDetails() {
      this.displayModal(ProductionStageLinksDialog, { 
        productionStageId: this.$data.stage.id,
      },
      this.$data.stage)
      .then((success) => {
        if(success) {
          this.successToast('Production Details Updated')
          this.reloadData()
        }
      })
    },

    editAvatarImage() {
      this.displayModal(EditLogoImageDialog, { 
        resourceId: this.$data.stage.id,
        APIresource: 'production/stages',
        currentURL: this.$data.stage.logoURL,
        circleCrop: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Logo Updated')
          this.requestData()
        }
      })
    },
    removeAvatarImage() {
      this.confirmDeleteMessage('Remove Image?', 'Are you sure you remove this stage\'s image?')
      .then(success => {
        if(success) {
          this.$apiService.coreService.deleteLogoImage(this.$data.stage.id, 'production/stages').then(response => {
            this.successToast('Image Removed')
            this.requestData()
          })
        }
      })
    },

    editComplianceImage() {
      this.displayModal(EditLogoImageDialog, { 
        resourceId: this.$data.stage.id,
        title: 'Edit Compliance Label Image',
        APIresource: 'production/stages',
        logoTypeName: 'compliancelabellogo',
        currentURL: this.$data.stage.complianceLabelURL,
        circleCrop: false
      })
      .then((success) => {
        if(success) {
          this.successToast('Compliance Image Updated')
          this.requestData()
        }
      })
    },
    removeComplianceImage() {
      this.confirmDeleteMessage('Remove Compliance Label Image?', 'Are you sure you remove this stage\'s compliance label image?')
      .then(success => {
        if(success) {
          this.$apiService.coreService.deleteLogoImage(this.$data.stage.id, 'production/stages', 'compliancelabellogo').then(response => {
            this.successToast('Compliance Image Removed')
            this.requestData()
          })
        }
      })
    },
    editAutoComplianceImage() {
      this.displayModal(EditLogoImageDialog, { 
        resourceId: this.$data.stage.id,
        title: 'Edit Automotive Label Image',
        APIresource: 'production/stages',
        logoTypeName: 'autocompliancelabellogo',
        currentURL: this.$data.stage.autoComplianceLabelURL,
        circleCrop: false
      })
      .then((success) => {
        if(success) {
          this.successToast('Automotive Image Updated')
          this.requestData()
        }
      })
    },
    removeAutoComplianceImage() {
      this.confirmDeleteMessage('Remove Automotive Label Image?', 'Are you sure you remove this stage\'s automotive compliance label image?')
      .then(success => {
        if(success) {
          this.$apiService.coreService.deleteLogoImage(this.$data.stage.id, 'production/stages', 'autocompliancelabellogo').then(response => {
            this.successToast('Automotive Image Removed')
            this.requestData()
          })
        }
      })
    },
    newProductionStation() {
      this.displayModal(ProductionStationDialog, { 
        isCreateMode: true,
        productionStageId: this.$data.stage.id
      })
      .then(stationId => {
        if(stationId) {
          this.successToast('Production Station Added')
          this.requestData()
        }
      })
    },
  }
}
</script>